import { useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import type { MiningMode, MiningPowerMode, MiningView } from "../types";
import type { MiningSnapshotSource } from "../state/use-mining-lifecycle";
import { miningTelemetryView } from "../state/view-mappers";
import { BoltIcon, CloseIcon, CpuIcon, GpuIcon, HourglassIcon, PauseIcon, PlayIcon, ShieldCheckIcon } from "../../../components/Icons";
import { formatElapsed, shortAddress } from "../../../components/format";
import type { CpuMiningSettings } from "../../../mining-engine/cpu-capacity";
import type { WalletSessionAction } from "../../../state/wallet-session-action";

interface MiningTerminalProps {
  mining: MiningView;
  telemetrySource?: MiningSnapshotSource | undefined;
  connected: boolean;
  sessionAction?: WalletSessionAction | null | undefined;
  walletBusy?: boolean;
  settingsBusy?: boolean;
  publicMiningEnabled: boolean;
  compact?: boolean;
  guidance?: ReactNode;
  onModeChange(mode: MiningMode): void;
  onPowerChange(mode: MiningPowerMode): void;
  onStart(): void;
  onPause(): void;
  onResume(): void;
  onRetrySubmission(): void;
  onChooseTier(): void;
  onConnect(): void;
  onCheckStatus?(): void;
  onNewSession?(): void;
  onSavedProof?(): void;
}

const phaseLabel: Record<MiningView["phase"], string> = {
  UNAVAILABLE: "UNAVAILABLE",
  LOCKED: "LOCKED",
  READY: "READY",
  STARTING: "STARTING",
  MINING: "MINING",
  PAUSED: "PAUSED",
  CANDIDATE_FOUND: "VERIFYING LOCALLY",
  SUBMITTING: "SUBMITTING PROOF",
  SUBMIT_RETRY: "PROOF SUBMISSION UNCONFIRMED",
  VERIFIED: "PROOF VERIFIED",
  EXPIRED: "CHALLENGE EXPIRED",
  ERROR: "ACTION REQUIRED",
};

const WORKLOAD_PRESETS = [1, 2, 4, 8];

function CpuWorkerControl({ cpu, disabled, saving, onChange }: {
  cpu: CpuMiningSettings; disabled: boolean; saving: boolean; onChange(value: number): void;
}) {
  const presets = WORKLOAD_PRESETS.filter(count => count <= cpu.workerLimit);
  const custom = !presets.includes(cpu.selectedWorkers);
  const [draft, setDraft] = useState(custom ? String(cpu.selectedWorkers) : "");
  const commit = () => {
    const value = Number(draft);
    setDraft(custom ? String(cpu.selectedWorkers) : "");
    if (!disabled && /^\d+$/.test(draft) && Number.isInteger(value) && value >= 1 &&
      value <= cpu.workerLimit && value !== cpu.selectedWorkers) onChange(value);
  };
  return <>
    <div className="power-options" role="group" aria-label="CPU workers">
      {presets.map(count => <button type="button" key={count}
        aria-pressed={cpu.selectedWorkers === count} disabled={disabled}
        onPointerDown={event => event.preventDefault()}
        onClick={() => { setDraft(""); if (count !== cpu.selectedWorkers) onChange(count); }}
        title={`${count} CPU worker${count === 1 ? "" : "s"}`}
      >x{count}</button>)}
      <label className={`workload-custom${custom ? " active" : ""}`}>
        Custom
        <input type="text" inputMode="numeric" aria-label="Custom CPU workers" value={draft}
          placeholder={`1–${cpu.workerLimit}`}
          disabled={disabled} aria-busy={saving || undefined}
          title={`Whole number from 1 to ${cpu.workerLimit}. Pause mining before changing workers.`}
          onChange={event => setDraft(event.target.value)} onBlur={commit}
          onKeyDown={event => {
            if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); }
            if (event.key === "Escape") setDraft(custom ? String(cpu.selectedWorkers) : "");
          }} />
      </label>
    </div>
  </>;
}

function WorkloadControl({ value, disabled, saving = false, onChange }: {
  value: number; disabled: boolean; saving?: boolean; onChange(value: number): void;
}) {
  const custom = !WORKLOAD_PRESETS.includes(value);
  const [draft, setDraft] = useState(custom ? String(value) : "");
  const commit = () => {
    const next = Number(draft);
    setDraft(custom ? String(value) : "");
    if (!disabled && /^\d+$/.test(draft) && Number.isInteger(next) && next >= 1 && next <= 64 && next !== value) {
      onChange(next);
    }
  };
  return <div className="power-options" role="group" aria-label="Mining workload">
    {WORKLOAD_PRESETS.map((preset) => <button type="button" key={preset}
      aria-pressed={value === preset} disabled={disabled}
      onPointerDown={(event) => event.preventDefault()}
      onClick={() => { setDraft(""); if (preset !== value) onChange(preset); }}
    >x{preset}</button>)}
    <label className={`workload-custom${custom ? " active" : ""}`}>
      Custom
      <input type="text" inputMode="numeric" aria-label="Custom workload" placeholder="1–64"
        title="Whole number from 1 to 64. Press Enter or leave the field to apply."
        value={draft} disabled={disabled} aria-busy={saving || undefined}
        onChange={(event) => setDraft(event.target.value)} onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); }
          if (event.key === "Escape") setDraft(custom ? String(value) : "");
        }} />
    </label>
  </div>;
}

export function MiningTerminal({ mining, telemetrySource, connected, sessionAction, walletBusy = false, settingsBusy = false, publicMiningEnabled, compact = false, guidance, onModeChange, onPowerChange,
  onStart, onPause, onResume, onRetrySubmission, onChooseTier, onConnect, onCheckStatus }: MiningTerminalProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [telemetryOpen, setTelemetryOpen] = useState(false);
  const consoleId = useId();
  const cpuInfoId = useId();
  const advancedWrapRef = useRef<HTMLDivElement>(null);
  const advancedButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!advancedOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (advancedWrapRef.current && !advancedWrapRef.current.contains(event.target as Node)) {
        setAdvancedOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setAdvancedOpen(false);
        queueMicrotask(() => advancedButtonRef.current?.focus());
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [advancedOpen]);
  const working = ["STARTING", "MINING", "CANDIDATE_FOUND", "SUBMITTING"].includes(mining.phase);
  const gpuKnownUnavailable = !mining.gpuAvailable && mining.gpuReason !== null;
  const savingVerifiedSettings = settingsBusy && mining.phase === "VERIFIED";


  return (
    <section className={`mining-terminal phase-${mining.phase.toLowerCase()}`} aria-labelledby="mining-title">
      <div className="terminal-chrome">
        <div className="terminal-title">
          <span className="terminal-lights" aria-hidden="true"><i /><i /><i /></span>
          <div><span>WORKSTATION 01</span><h2 id="mining-title">MINING TERMINAL</h2></div>
        </div>
        <span className={`terminal-phase ${working ? "active" : ""}`}>
          <i aria-hidden="true" /> {mining.phase === "EXPIRED" && mining.proofId ? "PROOF NO LONGER USABLE" : phaseLabel[mining.phase]}
        </span>
      </div>

      {mining.demoOnly ? <div className="demo-terminal-note">DEMO · SIMULATED DISPLAY · NO HASHING OR TRANSACTIONS</div> : null}

        <div className="mode-row">
          <span className="field-eyebrow">COMPUTE MODE</span>
          <div className="mode-switch" role="group" aria-label="Mining compute mode">
            <button
              type="button"
              aria-pressed={mining.mode === "GPU"}
              disabled={gpuKnownUnavailable || working || settingsBusy}
              aria-busy={savingVerifiedSettings && !gpuKnownUnavailable || undefined}
              title={gpuKnownUnavailable ? `GPU unavailable: ${mining.gpuReason}. CPU remains opt-in.` : !mining.gpuAvailable ? `GPU capability is checked when you select and start it.` : undefined}
              onClick={() => onModeChange("GPU")}
            ><GpuIcon /> GPU</button>
            <button
              type="button"
              aria-pressed={mining.mode === "CPU"}
              disabled={working || settingsBusy}
              aria-busy={savingVerifiedSettings || undefined}
              onClick={() => onModeChange("CPU")}
            ><CpuIcon /> CPU</button>
          </div>
        </div>
        {/* {gpuKnownUnavailable ? <p className="capability-note">GPU unavailable: {mining.gpuReason}. CPU remains opt-in.</p> :
        !mining.gpuAvailable ? <p className="capability-note">GPU capability is checked when you select and start it.</p> : null} */}

        <MiningReadout mining={mining} source={telemetrySource} working={working} />

      <details className="terminal-telemetry" open={!compact || telemetryOpen}
        onFocusCapture={(event) => {
          if (event.target !== event.currentTarget.querySelector("summary")) setTelemetryOpen(true);
        }}
        onToggle={(event) => { if (compact) setTelemetryOpen(event.currentTarget.open); }}>
        <summary>Worker settings</summary>
        <div className="power-row">
          <span>{mining.mode === "CPU" && mining.cpu ? <>CPU workers <span className="cpu-info">
            <button type="button" className="cpu-info-trigger" aria-label="CPU worker information" aria-describedby={cpuInfoId}>i</button>
            <span id={cpuInfoId} role="tooltip" className="cpu-info-tooltip">
              {mining.cpu.activeWorkers} workers running · {mining.cpu.logicalProcessors} logical threads reported by browser
              {mining.cpu.workerLimit < mining.cpu.logicalProcessors ? ` · App limit: ${mining.cpu.workerLimit} workers` : ""}.
              {working ? " Pause to change workers." : " Worker count does not guarantee a CPU usage percentage."}
            </span>
          </span></> : "WORKLOAD"}</span>
          {mining.mode === "CPU" && mining.cpu ?
            <CpuWorkerControl key={mining.cpu.selectedWorkers} cpu={mining.cpu}
              disabled={working || settingsBusy} saving={savingVerifiedSettings} onChange={onPowerChange} /> :
            <WorkloadControl key={mining.powerMode} value={mining.powerMode}
              disabled={working || settingsBusy} saving={savingVerifiedSettings} onChange={onPowerChange} />}
        </div>
      </details>

      {/* {guidance} */}

      <MiningCta
        miningSessionAction={mining.sessionAction} sessionChecking={mining.sessionChecking} onCheckStatus={onCheckStatus}
        sessionAction={sessionAction}
        settingsBusy={settingsBusy} walletBusy={walletBusy} phase={mining.phase}
        connected={connected} enabled={publicMiningEnabled || mining.demoOnly}
        onStart={onStart} onPause={onPause} onResume={onResume}
        onRetrySubmission={onRetrySubmission} canRetrySubmission={mining.canRetrySubmission}
        onChooseTier={onChooseTier} onConnect={onConnect}
        newChallengeBlocked={mining.newChallengeBlocked ?? false}
        retryMint={mining.mintRetryAvailable ?? false}
        canRestoreCheckpoint={mining.canRestoreCheckpoint ?? false}
      />
      {/* {["PAUSED", "ERROR", "SUBMIT_RETRY", "EXPIRED"].includes(mining.phase) ?
        <p className="capability-note" role="status">{mining.statusMessage}</p> : null}
      <p className="energy-note"><BoltIcon /> Mining uses your selected CPU/GPU and consumes electricity. A valid hash may arrive quickly or not before expiry.</p> */}

      <div className="advanced-console-wrap" ref={advancedWrapRef}>
        <button
          ref={advancedButtonRef}
          type="button"
          className="advanced-toggle"
          aria-expanded={advancedOpen}
          aria-haspopup="dialog"
          aria-controls={consoleId}
          onClick={() => setAdvancedOpen((open) => !open)}
        >
          <span>ADVANCED / PROTOCOL LOG</span><span>{advancedOpen ? "−" : "+"}</span>
        </button>
        {advancedOpen ? (
          <div
            id={consoleId}
            className="advanced-console advanced-console-popover"
            role="dialog"
            aria-label="Protocol and worker log"
          >
            <div className="advanced-console-header">
              <span className="advanced-console-title">PROTOCOL &amp; WORKER LOG</span>
              <button
                type="button"
                className="advanced-console-close"
                aria-label="Close protocol log"
                onClick={() => {
                  setAdvancedOpen(false);
                  queueMicrotask(() => advancedButtonRef.current?.focus());
                }}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="advanced-console-body">
              <dl>
                <div><dt>Challenge</dt><dd>{shortAddress(mining.challengeId, 12, 8)}</dd></div>
                <div><dt>Proof</dt><dd>{shortAddress(mining.proofId, 12, 8)}</dd></div>
                <div><dt>Rule</dt><dd>keccak256(commitment, nonce) &lt; target</dd></div>
              </dl>
              <ol aria-label="Mining log">
                {mining.logs.length === 0 ? <li className="muted">No worker events yet.</li> :
                  mining.logs.slice(-30).map((entry) => (
                    <li key={entry.id} className={`log-${entry.level.toLowerCase()}`}>
                      <time>{new Date(entry.at).toLocaleTimeString([], { hour12: false })}</time>
                      <span>{entry.message}</span>
                    </li>
                  ))}
              </ol>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

const noSubscription = () => () => { };
const noSnapshot = () => null;
function MiningReadout({ mining, source, working }: {
  mining: Pick<MiningView, "phase" | "measuredHashrate" | "attempts" | "activeElapsedMs" | "estimatedTimeMs" | "mode" | "difficultyLabel" | "challengeExpiresAt">;
  source?: MiningSnapshotSource | undefined;
  working: boolean;
}) {
  const snapshot = useSyncExternalStore(source?.subscribe ?? noSubscription, source?.getSnapshot ?? noSnapshot, noSnapshot);
  const telemetry = snapshot && !["LOCKED", "UNAVAILABLE"].includes(mining.phase) ? miningTelemetryView(snapshot) : mining;
  const remainingSeconds = mining.challengeExpiresAt === null ? null :
    Math.max(0, mining.challengeExpiresAt - Math.floor(Date.now() / 1_000));
  const remaining = remainingSeconds === null ? "—" : formatElapsed(remainingSeconds * 1_000);
  const estimateMs = useDisplayedEstimate(["LOCKED", "UNAVAILABLE"].includes(mining.phase) ? null : telemetry.estimatedTimeMs ?? null);
  const estimate = estimateMs === null ? "—" : estimateMs < 1_000 ? "<1s" :
    estimateMs > 365 * 24 * 60 * 60 * 1_000 ? ">1 year" : `~${formatElapsed(estimateMs)}`;
  const exceedsDeadline = mining.phase === "MINING" && telemetry.measuredHashrate !== null && estimateMs !== null &&
    remainingSeconds !== null && remainingSeconds > 0 && estimateMs > remainingSeconds * 1_000;
  return <>
    <div className="hashrate-readout">
      <span className="hashrate-label">MEASURED HASHRATE</span>
      <strong>{telemetry.measuredHashrate ?? "—"}</strong>
      <span>{telemetry.measuredHashrate === null ? "Worker idle" : `${mining.mode} worker`}</span>
      <div className={`hash-pulse ${working ? "active" : ""}`} aria-hidden="true">
        {Array.from({ length: 18 }, (_, index) => <i key={index} />)}
      </div>
    </div>
    <dl className="mining-stats">
      <div><dt>ACTIVE TIME · LOCAL</dt><dd>{mining.phase === "LOCKED" ? "—" : formatElapsed(telemetry.activeElapsedMs)}</dd></div>
      <div><dt>ATTEMPTS · LOCAL</dt><dd>{telemetry.attempts}</dd></div>
      <div><dt>DIFFICULTY</dt><dd>{mining.difficultyLabel}</dd></div>
      <div><dt>DEADLINE</dt><dd>{remaining}</dd></div>
      <div className={`mining-estimate${exceedsDeadline ? " exceeds-deadline" : ""}`}
        title={exceedsDeadline ? "Average solve time exceeds the challenge's remaining time. A valid hash may still arrive sooner." :
          "Measured estimate, refreshed at most once every 2 seconds. Mining is random; this is not a countdown or guarantee."}>
        <dt>EST. TIME</dt><dd>{estimate}</dd>
      </div>
    </dl>
  </>;
}

/** Throttle only the estimate readout; live throughput and mining keep their own cadence. */
function useDisplayedEstimate(estimate: number | null): number | null {
  const [displayed, setDisplayed] = useState(estimate);
  const publishedAt = useRef(performance.now());
  useEffect(() => {
    if (estimate === displayed) return;
    const publish = () => {
      publishedAt.current = performance.now();
      setDisplayed(estimate);
    };
    const remaining = 2_000 - (performance.now() - publishedAt.current);
    // First data and invalidated context should never wait behind an old sample.
    if (estimate === null || displayed === null || remaining <= 0) { publish(); return; }
    // A fresh sample replaces the pending value without postponing this deadline.
    const timer = setTimeout(publish, remaining);
    return () => clearTimeout(timer);
  }, [estimate, displayed]);
  return estimate === null ? null : displayed;
}

interface CtaProps {
  sessionChecking?: boolean | undefined;
  miningSessionAction?: MiningView["sessionAction"];
  onCheckStatus?: (() => void) | undefined;
  sessionAction?: WalletSessionAction | null | undefined;
  newChallengeBlocked: boolean;
  retryMint: boolean;
  canRestoreCheckpoint: boolean;
  settingsBusy: boolean;
  walletBusy: boolean;
  phase: MiningView["phase"];
  connected: boolean;
  enabled: boolean;
  onStart(): void;
  onPause(): void;
  onResume(): void;
  onRetrySubmission(): void;
  canRetrySubmission: boolean;
  onChooseTier(): void;
  onConnect(): void;
}

function MiningCta({ phase, connected, sessionAction, miningSessionAction, sessionChecking, onCheckStatus, walletBusy, settingsBusy, enabled, onStart, onPause, onResume, onRetrySubmission,
  canRetrySubmission, onChooseTier, onConnect, newChallengeBlocked, retryMint, canRestoreCheckpoint }: CtaProps) {
  if (phase === "MINING") return <button type="button" className="terminal-cta pause" onClick={onPause}><PauseIcon /> PAUSE MINING</button>;
  if (sessionAction) return <button type="button" className="terminal-cta" disabled={sessionAction.disabled}
    aria-busy={sessionAction.kind === "wait" || undefined} onClick={onConnect}><PlayIcon />
    {sessionAction.kind === "connect" ? "CONNECT TO BEGIN" : sessionAction.label.toUpperCase()}</button>;
  // Saving settings must keep the verified presentation (including its glow)
  // stable while retaining the existing action lock until persistence settles.
  if (phase === "VERIFIED" && connected && !walletBusy) return <button type="button" className="terminal-cta verified"
    disabled={settingsBusy || newChallengeBlocked} aria-busy={settingsBusy || undefined}
    title={newChallengeBlocked ? "A transaction is unresolved. View its status in Activities; minting unlocks after a verified failure." : undefined}
    onClick={onChooseTier}><ShieldCheckIcon /> {newChallengeBlocked ? "TRANSACTION PENDING" : retryMint ? "CHOOSE TIER AGAIN" : "CHOOSE NFT TIER"}</button>;
  if (settingsBusy) return <button type="button" className="terminal-cta" disabled aria-busy="true"><HourglassIcon /> SAVING MINING SETTINGS</button>;
  if (walletBusy) return <button type="button" className="terminal-cta" disabled aria-busy="true"><HourglassIcon /> COMPLETE WALLET REQUEST</button>;
  if (!connected) return <button type="button" className="terminal-cta" onClick={onConnect}><PlayIcon /> CONNECT TO BEGIN</button>;
  if (miningSessionAction) return <button type="button" className="terminal-cta"
    disabled={miningSessionAction === "blocked" || (miningSessionAction === "new" && (newChallengeBlocked || !enabled))}
    aria-busy={sessionChecking || undefined}
    onClick={miningSessionAction === "check" ? onCheckStatus : ["resume", "transfer"].includes(miningSessionAction) ? onResume : onStart}>
    <PlayIcon />{miningSessionAction === "check" ? "CHECK STATUS" : miningSessionAction === "resume" ? "RESUME MINING" :
      miningSessionAction === "transfer" ? "CONTINUE HERE" : miningSessionAction === "blocked" ? sessionChecking ? "CHECKING SESSION…" : "MINING NOT AVAILABLE" : "START MINING"}</button>;
  if (phase === "PAUSED") return <button type="button" className="terminal-cta" onClick={onResume}><PlayIcon /> RESUME MINING</button>;
  if (phase === "SUBMIT_RETRY") return <button type="button" className="terminal-cta"
    disabled={!canRetrySubmission} onClick={onRetrySubmission}><PlayIcon />
    {canRetrySubmission ? "RETRY PROOF SUBMISSION" : "PROOF RECOVERY UNAVAILABLE"}</button>;
  if (phase === "STARTING" || phase === "CANDIDATE_FOUND" || phase === "SUBMITTING") {
    return <button type="button" className="terminal-cta" disabled><HourglassIcon /> {phaseLabel[phase]}</button>;
  }
  if (!enabled && canRestoreCheckpoint) return <button type="button" className="terminal-cta" onClick={onStart}><PlayIcon /> RESTORE SAVED MINING</button>;
  if (!enabled) return <button type="button" className="terminal-cta" disabled><ShieldCheckIcon /> MINING DISABLED</button>;
  if (newChallengeBlocked) return <button type="button" className="terminal-cta" disabled><HourglassIcon /> RESOLVE PENDING TRANSACTION</button>;
  if (phase === "EXPIRED") return <button type="button" className="terminal-cta" onClick={onStart}><PlayIcon /> REQUEST NEW CHALLENGE</button>;
  return <button type="button" className="terminal-cta" onClick={onStart}><PlayIcon /> START MINING</button>;
}
